import React, { useState, useEffect, useRef } from 'react';
import './App.css'; // Assuming you might want a general App.css
import Lobby from './components/Lobby';
import GameRoom from './components/GameRoom';
import { createConsumer } from '@rails/actioncable'; // Import Action Cable
import SoundService from './services/SoundService'; // Import SoundService

const BASE_CABLE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001/cable';

function App() {
  const [currentUser, setCurrentUser] = useState(null); // { id, username }
  const [currentGameRoom, setCurrentGameRoom] = useState(null); // Full game room object from backend
  const [cable, setCable] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false); // Track user interaction for sound loading
  const [isMuted, setIsMuted] = useState(SoundService.getMuteState()); // Initialize mute state

  const gameRoomRef = useRef(currentGameRoom);
  useEffect(() => {
    gameRoomRef.current = currentGameRoom;
  }, [currentGameRoom]);

  // Effect for initializing sounds after user interaction
  useEffect(() => {
    if (hasInteracted) {
      console.log('User has interacted, loading sounds...');
      SoundService.loadSounds();
    }
  }, [hasInteracted]);

  // Effect to create/destroy Action Cable consumer when currentUser changes
  useEffect(() => {
    let cableConsumerInstance = null;
    if (currentUser && currentUser.id) {
      console.log(`User identified (ID: ${currentUser.id}), creating Action Cable consumer.`);
      const cableUrlWithUser = `${BASE_CABLE_URL}?user_id=${currentUser.id}`;
      cableConsumerInstance = createConsumer(cableUrlWithUser);
      setCable(cableConsumerInstance);
    } else {
      // If no current user, ensure any existing cable connection is closed
      // and cable state is nullified.
      if (cable) {
        console.log('No current user or user logged out, disconnecting existing cable consumer.');
        cable.disconnect();
        setCable(null);
      }
    }

    return () => {
      // Cleanup function: if a consumer was created in this effect run, disconnect it.
      if (cableConsumerInstance) {
        console.log('Cleaning up cable consumer on currentUser change or component unmount.');
        cableConsumerInstance.disconnect();
      }
      // If currentUser becomes null (logout), cable state is already set to null above.
      // No need to setCable(null) here again if we are only depending on currentUser.
    };
  }, [currentUser]); // Corrected: Only depend on currentUser

  // Effect to manage subscription when cable or currentGameRoom.game_room.code changes
  useEffect(() => {
    let activeSubscription = null;
    if (cable && currentGameRoom && currentGameRoom.game_room && currentGameRoom.game_room.code) {
      const roomCode = currentGameRoom.game_room.code;
      console.log(`[App.js Subscription Effect] Attempting to subscribe to GameRoomChannel for room ${roomCode}.`);
      
      activeSubscription = cable.subscriptions.create(
        { channel: 'GameRoomChannel', room_code: roomCode },
        {
          received: (data) => {
            console.log(`[App.js ActionCable] RAW MESSAGE RECEIVED ON CHANNEL. Type: ${data?.type}, Room Code in Data: ${data?.game_room?.code || data?.game_state?.game_room?.code || 'N/A'}. Current gameRoomRef.current?.game_room?.code: ${gameRoomRef.current?.game_room?.code}`, data);

            const currentRoomState = gameRoomRef.current; 
            // Check if data is for the current room using room code
            if (data.game_state?.game_room?.code !== currentRoomState?.game_room?.code && 
                data.game_room?.code !== currentRoomState?.game_room?.code) {
                // If the message has game_state, check its code. If it has game_room directly, check its code.
                // If neither matches, and it's not a global message type we expect without specific room data, log and potentially ignore.
                if (data.type !== 'player_disconnected_simple') { // Example: player_disconnected_simple might be broadcast more broadly or handled differently
                    console.warn(`[App.js ActionCable Received] Data for room ${data.game_state?.game_room?.code || data.game_room?.code} but current room is ${currentRoomState?.game_room?.code}. No state update.`);
                    return; // Exit if message is not for the current room, for most types
                }
            }

            console.log(`[App.js ActionCable Received Raw] Room ${roomCode} (subscription context), Data:`, data);

            if (data.type === 'player_joined') {
              // Validate currentRoomState and its game_room property before accessing nested properties
              const currentCode = currentRoomState?.game_room?.code;
              const currentId = currentRoomState?.game_room?.id;
              const currentPlayersLength = currentRoomState?.players?.length;

              if (data.game_room && data.game_room.code === currentCode) {
                console.log(`[App.js ActionCable Received] Type: player_joined. Target Room Code in data: ${data.game_room?.code}. Current Context Room Code: ${currentCode}`);
                console.log(`[App.js ActionCable Received player_joined] Updating currentGameRoom. Prev player count (from currentRoomState): ${currentPlayersLength}`);
                
                setCurrentGameRoom(prevState => {
                  if (!prevState || !prevState.game_room || prevState.game_room.id !== data.game_room.id) {
                    console.warn(`[App.js ActionCable Received player_joined] prevState validation failed. Not updating. PrevState GameRoom ID: ${prevState?.game_room?.id}, Data GameRoom ID: ${data.game_room?.id}`);
                    return prevState; 
                  }
                  
                  console.log(`[App.js ActionCable Received player_joined] prevState game_room.code: ${prevState.game_room.code}, data.game_room.code: ${data.game_room.code}`);
                  console.log('[App.js player_joined] About to update. prevState.players:', JSON.stringify(prevState.players));
                  console.log('[App.js player_joined] Received data.player (the new player):', JSON.stringify(data.player));
                  console.log('[App.js player_joined] Received data.game_room (full from broadcast):', JSON.stringify(data.game_room));
                  console.log('[App.js player_joined] Specifically, data.game_room.player_list from broadcast:', JSON.stringify(data.game_room.player_list));
                  
                  const newPlayersList = data.game_room.player_list || [];
                  console.log('[App.js player_joined] newPlayersList that will be set to state.players:', JSON.stringify(newPlayersList));
                  
                  const newState = {
                    ...prevState,
                    game_room: data.game_room,
                    players: newPlayersList,
                  };
                  console.log('[App.js player_joined] New combined state to be set (first 500 chars):', JSON.stringify(newState).substring(0, 500) + "...");
                  return newState;
                });
                SoundService.playSound('playerJoin');
              } else {
                console.warn(`[App.js ActionCable Received player_joined] Data for room ${data.game_room?.code} (ID: ${data.game_room?.id}) but current room is ${currentCode} (ID: ${currentId}). No state update.`);
              }
            } else if (data.type === 'game_started') {
              if (data.game_state && data.game_state.game_room && data.game_state.game_room.code === currentRoomState?.game_room?.code) {
                console.log(`[App.js ActionCable Received] Type: game_started. Updating game state for room ${currentRoomState.game_room.id}.`);
                console.log('[App.js ActionCable game_started] Incoming data:', JSON.parse(JSON.stringify(data)));
                console.log('[App.js ActionCable game_started] Current gameRoomRef.current before update:', JSON.parse(JSON.stringify(gameRoomRef.current)));
                setCurrentGameRoom(data.game_state);
                SoundService.playBackgroundMusic(); // Play background music on game start
              } else {
                console.warn(`[App.js ActionCable Received game_started] Data for game_state.game_room ${data.game_state?.game_room?.id} but current room is ${currentRoomState.game_room.id}. No state update.`);
              }
            } else if (data.type === 'card_played') {
              if (data.game_state && data.game_state.game_room && data.game_state.game_room.code === currentRoomState?.game_room?.code) {
                console.log(`[App.js ActionCable Received] Type: card_played. Data:`, data);
                setCurrentGameRoom(data.game_state);
                // Play different sound for special cards vs regular cards
                if (data.card && (data.card.card_type === 'wild' || data.card.card_type === 'wild_draw_four' || data.card.card_type === 'skip' || data.card.card_type === 'reverse' || data.card.card_type === 'draw_two')) {
                  SoundService.playSound('specialCard');
                } else {
                  SoundService.playSound('playCard');
                }
              } else {
                 console.warn(`[App.js ActionCable Received card_played] Data for game_state.game_room ${data.game_state?.game_room?.id} but current room is ${currentRoomState.game_room.id}. No state update.`);
              }
            } else if (data.type === 'card_drawn') {
              if (data.game_state && data.game_state.game_room && data.game_state.game_room.code === currentRoomState?.game_room?.code) {
                console.log(`[App.js ActionCable Received] Type: card_drawn. Updating game state for room ${currentRoomState.game_room.id}.`);
                setCurrentGameRoom(data.game_state);
                SoundService.playSound('drawCard'); // Play specific draw card sound
              } else {
                console.warn(`[App.js ActionCable Received card_drawn] Data for game_state.game_room ${data.game_state?.game_room?.id} but current room is ${currentRoomState.game_room.id}. No state update.`);
              }
            } else if (data.type === 'player_disconnected') { // This type is now player_disconnected_simple from channel
                // Assuming player_disconnected_simple has game_room.code or top-level code
                 if (data.game_room && data.game_room.code === currentRoomState?.game_room?.code) {
                    setCurrentGameRoom(prevState => ({ ...prevState, game_room: data.game_room, players: data.game_room.players || [] }));
                 } else if (data.player_id && currentRoomState?.players?.some(p => p.id === data.player_id)) {
                    // Fallback for simple disconnect if full room data not resent
                    setCurrentGameRoom(prevState => {
                        if (!prevState || !prevState.players) return prevState;
                        const updatedPlayers = prevState.players.filter(p => p.user_id !== data.user_id);
                        return { ...prevState, game_room: { ...prevState.game_room, players: updatedPlayers, player_count: updatedPlayers.length }, players: updatedPlayers }; 
                    });
                 }
            } else if (data.type === 'game_over') {
              if (data.game_state && data.game_state.game_room && data.game_state.game_room.code === currentRoomState?.game_room?.code) {
                 console.log(`[App.js ActionCable Received] Game Over in room ${currentRoomState.game_room.id}.`);
                setCurrentGameRoom(data.game_state);
                alert(`Game Over! Winner: ${data.game_state.winner?.username || 'N/A'}`);
                SoundService.playSound('gameWin');
                SoundService.stopBackgroundMusic(); // Stop background music on game over
              }
            } else if (data.type === 'game_aborted') {
              if (data.game_room_code === currentRoomState?.game_room?.code) {
                console.log(`[App.js ActionCable Received] Game Aborted in room ${currentRoomState.game_room.code}. Reason: ${data.reason}`);
                alert(`Game Aborted: ${data.reason || 'A player left.'}`);
                handleLeaveRoom(); // This should take the user back to the lobby
                SoundService.stopBackgroundMusic(); // Ensure music stops
              }
            } else if (data.type === 'player_left_waiting_room') {
              // Handle player leaving a waiting room (e.g., update player list/count)
              if (data.game_room_code === currentRoomState?.game_room?.code) {
                console.log(`[App.js ActionCable Received] Player left waiting room ${currentRoomState.game_room.code}. User: ${data.username}, Remaining: ${data.players_remaining_count}`);
                // Option 1: Refetch game room state for simplicity if detailed player list is needed
                // Option 2: Update local state if only count or a simple message is shown
                setCurrentGameRoom(prevState => {
                  if (!prevState || !prevState.game_room || prevState.game_room.code !== data.game_room_code) {
                    return prevState;
                  }
                  // Create a new players array excluding the one who left
                  // This is a bit tricky as we only have user_id of the leaver from the broadcast.
                  // The broadcast sends player_id, user_id, username. We need to update players list in game_room.players
                  const updatedPlayers = prevState.players.filter(p => p.user_id !== data.user_id);
                  return {
                    ...prevState,
                    game_room: {
                      ...prevState.game_room,
                      players: updatedPlayers,
                      player_count: data.players_remaining_count
                    },
                    players: updatedPlayers // also update the top-level players array if used directly by GameRoom component
                  };
                });
                // Potentially play a sound for player leaving waiting room
                 SoundService.playSound('playerLeave'); // Assuming you have a 'playerLeave' sound
              }
            } else {
              console.log(`[App.js ActionCable Received] Unhandled message type: ${data.type}`);
            }
          },
          connected: () => {
            console.log(`[App.js Subscription Effect] Successfully subscribed to GameRoomChannel for room ${roomCode}`);
          },
          disconnected: () => {
            console.log(`[App.js Subscription Effect] Disconnected from GameRoomChannel for room ${roomCode}`);
          }
        }
      );
      setSubscription(activeSubscription);
    } else {
      // Conditions for subscription not met (e.g., cable is null, or no current room id)
      // If there's an existing subscription in state, it means we were subscribed, but now shouldn't be.
      if (subscription) {
        console.log(`[App.js Subscription Effect] Conditions no longer met. Unsubscribing from room ${subscription.identifier ? JSON.parse(subscription.identifier).room_id : 'N/A'}.`);
        subscription.unsubscribe();
        setSubscription(null);
      }
    }

    return () => {
      // This cleanup runs if an 'activeSubscription' was created in THIS effect run.
      if (activeSubscription) {
        const subId = activeSubscription.identifier ? JSON.parse(activeSubscription.identifier).room_id : 'N/A';
        console.log(`[App.js Subscription Effect Cleanup] Unsubscribing from GameRoomChannel (room ${subId}) for the subscription created in this effect run.`);
        activeSubscription.unsubscribe();
        // Only set subscription to null if the one being cleaned up is the one currently in our state.
        // This avoids nulling out a newer subscription if effects run in a complex sequence (e.g. StrictMode).
        setSubscription(currentSubInState => {
          if (currentSubInState === activeSubscription) {
            console.log(`[App.js Subscription Effect Cleanup] Setting subscription state to null for room ${subId}.`);
            return null;
          }
          console.log(`[App.js Subscription Effect Cleanup] Subscription in state (room ${currentSubInState?.identifier ? JSON.parse(currentSubInState.identifier).room_id : 'N/A'}) is different from the one being cleaned up (room ${subId}). Not nulling state.`);
          return currentSubInState; 
        });
      }
    };
  }, [cable, currentGameRoom?.game_room?.code]); // Dependency updated to game_room.code

  const handleJoinGame = (user, gameRoomFromApi) => {
    if (!hasInteracted) setHasInteracted(true); // Set interaction on first join/create action
    setCurrentUser(user);
    // Transform the gameRoomFromApi (which is the game_room object itself)
    // into the structure expected by GameRoom.js component and ActionCable updates
    const initialGameState = {
      game_room: gameRoomFromApi, // The API response is the game_room details
      players: gameRoomFromApi.players || [], // players are part of gameRoomFromApi
      top_card: null, // No top_card when game is just created/joined
      draw_pile_size: 0, // No draw pile yet
      // Other fields like status, current_color, turn_player_id are in gameRoomFromApi
    };
    setCurrentGameRoom(initialGameState);
  };

  const handleGameUpdate = (updatedGameRoomData) => {
    setCurrentGameRoom(updatedGameRoomData);
  };

  const handleLeaveRoom = () => {
    if (subscription) {
      subscription.unsubscribe();
    }
    setCurrentGameRoom(null);
    setSubscription(null);
    setError(''); 
    SoundService.stopBackgroundMusic(); // Stop background music on leaving room
  };

  const handleToggleMute = () => {
    const newMuteState = SoundService.toggleMute();
    setIsMuted(newMuteState);
  };

  return (
    <div className="App" onClick={() => !hasInteracted && setHasInteracted(true)}>
      {error && <p className="app-error-message">App Error: {error}</p>}
      
      {/* Mute button should appear on all pages */}
      <div className="app-header-controls">
        <button onClick={handleToggleMute} className="mute-button">
          {isMuted ? '🔇 Unmute' : '🔊 Mute'}
        </button>
      </div>

      {currentGameRoom ? (
        <GameRoom 
          gameRoomData={currentGameRoom} 
          currentUser={currentUser} 
          onLeaveRoom={handleLeaveRoom}
          onGameUpdate={handleGameUpdate}
          isMuted={isMuted}
        />
      ) : (
        <Lobby onJoinGame={handleJoinGame} currentUser={currentUser} setCurrentUser={setCurrentUser}/>
      )}
    </div>
  );
}

export default App;
